// Solar time calculations — NOAA / Meeus formulation
// Accurate to a few seconds for dates between 1800 and 2200.
// References:
//   NOAA Solar Calculator spreadsheet
//   Jean Meeus, "Astronomical Algorithms" (2nd ed.) chapters 25 & 28

const deg2rad = (d: number) => (d * Math.PI) / 180;
const rad2deg = (r: number) => (r * 180) / Math.PI;

/** Day index (0-based) of `date`'s calendar day within its year, DST-safe. */
export function dayIndex(date: Date): number {
  const y = date.getFullYear();
  return Math.round(
    (Date.UTC(y, date.getMonth(), date.getDate()) - Date.UTC(y, 0, 1)) / 86_400_000,
  );
}

/** Julian Day for a Date (UTC). */
export function julianDay(date: Date): number {
  return date.getTime() / 86_400_000 + 2440587.5;
}

/** Julian centuries since J2000.0 (TT ≈ UT for our precision). */
function julianCenturies(date: Date): number {
  return (julianDay(date) - 2451545) / 36525;
}

/* ------------------------------------------------------------------ */
/*  Core NOAA / Meeus solar parameters                                */
/* ------------------------------------------------------------------ */

export interface SolarGeometry {
  T: number;                 // Julian centuries since J2000
  meanLongitude: number;     // L0, degrees (0..360)
  meanAnomaly: number;       // M, degrees
  eccentricity: number;      // e, unitless
  equationOfCentre: number;  // C, degrees
  trueLongitude: number;     // L0 + C, degrees
  apparentLongitude: number; // λ, degrees (nutation + aberration corrected)
  meanObliquity: number;     // ε0, degrees
  obliquity: number;         // ε (corrected), degrees
  declination: number;       // δ, degrees
}

// Equation of centre in degrees (Meeus ch. 25).
function equationOfCentreDeg(T: number, Mrad: number): number {
  return (
    (1.914602 - 0.004817 * T - 0.000014 * T * T) * Math.sin(Mrad) +
    (0.019993 - 0.000101 * T) * Math.sin(2 * Mrad) +
    0.000289 * Math.sin(3 * Mrad)
  );
}

// Mean obliquity of the ecliptic in degrees, Meeus eq. 22.2 (arc-seconds unrolled).
function meanObliquityDeg(T: number): number {
  const seconds = 21.448 - T * (46.815 + T * (0.00059 - T * 0.001813));
  return 23 + (26 + seconds / 60) / 60;
}

export function solarGeometry(date: Date): SolarGeometry {
  const T = julianCenturies(date);

  const L0 = (280.46646 + 36000.76983 * T + 0.0003032 * T * T) % 360;
  const meanLongitude = L0 < 0 ? L0 + 360 : L0;
  const meanAnomaly = 357.52911 + 35999.05029 * T - 0.0001537 * T * T;
  const eccentricity = 0.016708634 - 0.000042037 * T - 0.0000001267 * T * T;
  const equationOfCentre = equationOfCentreDeg(T, deg2rad(meanAnomaly));
  const trueLongitude = meanLongitude + equationOfCentre;

  // Longitude of the Moon's ascending node, used for nutation
  const omega = deg2rad(125.04 - 1934.136 * T);
  const apparentLongitude = trueLongitude - 0.00569 - 0.00478 * Math.sin(omega);
  const meanObliquity = meanObliquityDeg(T);
  const obliquity = meanObliquity + 0.00256 * Math.cos(omega);
  const declination = rad2deg(
    Math.asin(Math.sin(deg2rad(obliquity)) * Math.sin(deg2rad(apparentLongitude))),
  );

  return {
    T,
    meanLongitude,
    meanAnomaly,
    eccentricity,
    equationOfCentre,
    trueLongitude,
    apparentLongitude,
    meanObliquity,
    obliquity,
    declination,
  };
}

/* ------------------------------------------------------------------ */
/*  Equation of time, split into physical components                  */
/* ------------------------------------------------------------------ */

export interface EquationOfTimeBreakdown {
  total: number;         // minutes
  obliquity: number;     // minutes — contribution from axial tilt
  eccentricity: number;  // minutes — contribution from elliptical orbit
  crossTerm: number;     // minutes — small coupling, usually < 0.1 min
}

/**
 * Equation of Time, broken into its physical components.
 *
 * Starting from NOAA's compact form:
 *   EoT = 4 · [ y·sin(2L0) − 2e·sin(M) + 4ey·sin(M)·cos(2L0)
 *                − ½y²·sin(4L0) − 1¼e²·sin(2M) ]
 *   where y = tan²(ε/2)
 *
 * We group:
 *   obliquity    = 4 · [ y·sin(2L0) − ½y²·sin(4L0) ]
 *   eccentricity = 4 · [ −2e·sin(M) − 1¼e²·sin(2M) ]
 *   crossTerm    = 4 · [ 4ey·sin(M)·cos(2L0) ]
 *
 * Result is in minutes (NOAA's "4" converts degrees → minutes of time).
 */
export function equationOfTimeBreakdown(date: Date): EquationOfTimeBreakdown {
  const g = solarGeometry(date);
  const L0 = deg2rad(g.meanLongitude);
  const M = deg2rad(g.meanAnomaly);
  const e = g.eccentricity;
  const y = Math.tan(deg2rad(g.obliquity) / 2) ** 2;

  const obliquity =
    4 * rad2deg(y * Math.sin(2 * L0) - 0.5 * y * y * Math.sin(4 * L0));
  const eccentricity =
    4 *
    rad2deg(-2 * e * Math.sin(M) - 1.25 * e * e * Math.sin(2 * M));
  const crossTerm =
    4 * rad2deg(4 * e * y * Math.sin(M) * Math.cos(2 * L0));

  return {
    obliquity,
    eccentricity,
    crossTerm,
    total: obliquity + eccentricity + crossTerm,
  };
}

export function equationOfTimeMinutes(date: Date): number {
  return equationOfTimeBreakdown(date).total;
}

/* ------------------------------------------------------------------ */
/*  Longitude correction                                              */
/* ------------------------------------------------------------------ */

export function longitudeCorrectionMinutes(
  longitude: number,
  tzOffsetMinutes: number,
): number {
  const standardMeridian = (tzOffsetMinutes / 60) * 15;
  return 4 * (longitude - standardMeridian);
}

/* ------------------------------------------------------------------ */
/*  Public result shape                                               */
/* ------------------------------------------------------------------ */

export interface SolarResult {
  trueSolarTime: Date;
  offsetMinutes: number;
  equationOfTime: number;
  obliquityComponent: number;
  eccentricityComponent: number;
  longitudeCorrection: number;
  declination: number;      // degrees
  obliquity: number;        // degrees (ε)
  eccentricity: number;     // unitless (e)
  altitude: number;         // degrees above horizon (no refraction)
  azimuth: number;          // degrees clockwise from true north
  solarNoon: Date | null;
  sunrise: Date | null;
  sunset: Date | null;
  dayLengthMinutes: number | null;
}

export function computeSolar(
  clockTime: Date,
  latitude: number,
  longitude: number,
  tzOffsetMinutes: number,
): SolarResult {
  const eot = equationOfTimeBreakdown(clockTime);
  const geom = solarGeometry(clockTime);
  const lonCorr = longitudeCorrectionMinutes(longitude, tzOffsetMinutes);
  const offsetMinutes = eot.total + lonCorr;

  const trueSolarTime = new Date(clockTime.getTime() + offsetMinutes * 60_000);

  // Midnight of the place's current calendar day, using the place's own UTC offset.
  const shifted = clockTime.getTime() + tzOffsetMinutes * 60_000;
  const midnightMs = Math.floor(shifted / 86_400_000) * 86_400_000 - tzOffsetMinutes * 60_000;
  const solarNoon = new Date(midnightMs + 12 * 3_600_000 - offsetMinutes * 60_000);

  const { sunrise, sunset } = sunriseSunset(
    midnightMs,
    latitude,
    longitude,
    tzOffsetMinutes,
    geom.declination,
    eot.total,
  );
  const dayLengthMinutes =
    sunrise && sunset ? (sunset.getTime() - sunrise.getTime()) / 60_000 : null;

  const tstMinutes = ((((shifted + offsetMinutes * 60_000) / 60_000) % 1440) + 1440) % 1440;
  const { altitude, azimuth } = sunPosition(tstMinutes, latitude, geom.declination);

  return {
    trueSolarTime,
    offsetMinutes,
    equationOfTime: eot.total,
    obliquityComponent: eot.obliquity + eot.crossTerm, // fold tiny cross term in
    eccentricityComponent: eot.eccentricity,
    longitudeCorrection: lonCorr,
    declination: geom.declination,
    obliquity: geom.obliquity,
    eccentricity: geom.eccentricity,
    altitude,
    azimuth,
    solarNoon,
    sunrise,
    sunset,
    dayLengthMinutes,
  };
}

/* ------------------------------------------------------------------ */
/*  Sunrise / sunset                                                  */
/* ------------------------------------------------------------------ */

function sunriseSunset(
  midnightMs: number,
  latitude: number,
  longitude: number,
  tzOffsetMinutes: number,
  declinationDeg: number,
  eotMinutes: number,
): { sunrise: Date | null; sunset: Date | null } {
  const latRad = deg2rad(latitude);
  const declRad = deg2rad(declinationDeg);

  // 0.833° accounts for atmospheric refraction + solar disk radius
  const cosH =
    (Math.cos(deg2rad(90.833)) - Math.sin(latRad) * Math.sin(declRad)) /
    (Math.cos(latRad) * Math.cos(declRad));

  if (cosH > 1 || cosH < -1) {
    return { sunrise: null, sunset: null };
  }
  const H = rad2deg(Math.acos(cosH));

  const lonCorr = longitudeCorrectionMinutes(longitude, tzOffsetMinutes);
  const solarNoonMin = 12 * 60 - (eotMinutes + lonCorr);

  const sunriseMin = solarNoonMin - (H / 15) * 60;
  const sunsetMin = solarNoonMin + (H / 15) * 60;

  return {
    sunrise: new Date(midnightMs + sunriseMin * 60_000),
    sunset: new Date(midnightMs + sunsetMin * 60_000),
  };
}

/* ------------------------------------------------------------------ */
/*  Sun position, day length, EoT zero crossings                      */
/* ------------------------------------------------------------------ */

// tstMinutes: true solar time as minutes after sundial midnight.
export function sunPosition(
  tstMinutes: number,
  latitude: number,
  declinationDeg: number,
): { altitude: number; azimuth: number } {
  const ha = deg2rad(tstMinutes / 4 - 180);
  const lat = deg2rad(latitude);
  const dec = deg2rad(declinationDeg);
  const altitude = rad2deg(
    Math.asin(Math.sin(lat) * Math.sin(dec) + Math.cos(lat) * Math.cos(dec) * Math.cos(ha)),
  );
  const az = rad2deg(
    Math.atan2(Math.sin(ha), Math.cos(ha) * Math.sin(lat) - Math.tan(dec) * Math.cos(lat)),
  );
  return { altitude, azimuth: (az + 180 + 360) % 360 };
}

// Hours of daylight; 0 during polar night, 24 during midnight sun.
export function dayLengthHours(date: Date, latitude: number): number {
  const dec = deg2rad(solarGeometry(date).declination);
  const lat = deg2rad(latitude);
  const cosH =
    (Math.cos(deg2rad(90.833)) - Math.sin(lat) * Math.sin(dec)) /
    (Math.cos(lat) * Math.cos(dec));
  const H = rad2deg(Math.acos(Math.max(-1, Math.min(1, cosH))));
  return (2 * H) / 15;
}

// All moments in `year` when the equation of time crosses zero (normally four).
export function eotZeroCrossings(year: number): Date[] {
  const out: Date[] = [];
  let prevT = new Date(year, 0, 1, 12).getTime();
  let prev = equationOfTimeMinutes(new Date(prevT));
  for (let i = 1; i <= 366; i++) {
    const d = new Date(year, 0, 1 + i, 12);
    if (d.getFullYear() !== year) break;
    const cur = equationOfTimeMinutes(d);
    if (Math.sign(prev) !== Math.sign(cur)) {
      out.push(new Date(prevT + (prev / (prev - cur)) * (d.getTime() - prevT)));
    }
    prevT = d.getTime();
    prev = cur;
  }
  return out;
}

// Next moment after `from` when EoT crosses zero, interpolated between daily samples.
export function nextEotZero(from: Date): Date | null {
  const DAY = 86_400_000;
  let prevT = from.getTime();
  let prev = equationOfTimeMinutes(from);
  for (let i = 1; i <= 400; i++) {
    const t = from.getTime() + i * DAY;
    const cur = equationOfTimeMinutes(new Date(t));
    if (Math.sign(prev) !== Math.sign(cur)) {
      return new Date(prevT + (prev / (prev - cur)) * DAY);
    }
    prevT = t;
    prev = cur;
  }
  return null;
}

// Noon-of-each-day dates for a calendar year (365 or 366 entries).
export function daysOfYear(year: number): Date[] {
  const out: Date[] = [];
  for (let i = 0; ; i++) {
    const d = new Date(year, 0, 1 + i, 12);
    if (d.getFullYear() !== year) return out;
    out.push(d);
  }
}

export interface AnalemmaPoint {
  eot: number; // minutes
  dec: number; // degrees
}

export function analemmaPoints(year: number): AnalemmaPoint[] {
  return daysOfYear(year).map((d) => ({
    eot: equationOfTimeMinutes(d),
    dec: solarGeometry(d).declination,
  }));
}

export function dayLengthSeries(year: number, latitude: number): number[] {
  return daysOfYear(year).map((d) => dayLengthHours(d, latitude));
}

/* ------------------------------------------------------------------ */
/*  Formatting                                                        */
/* ------------------------------------------------------------------ */

export function formatTime(d: Date, timeZone?: string): string {
  return d.toLocaleTimeString(undefined, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: true,
  });
}

export function formatTimeShort(d: Date, timeZone?: string): string {
  return d.toLocaleTimeString(undefined, {
    timeZone,
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
}

export function formatMinutesSigned(mins: number): string {
  const sign = mins >= 0 ? "+" : "−";
  const totalSec = Math.round(Math.abs(mins) * 60);
  const whole = Math.floor(totalSec / 60);
  const sec = totalSec % 60;
  return `${sign}${whole}m ${sec.toString().padStart(2, "0")}s`;
}

export function formatDuration(mins: number): string {
  const total = Math.round(mins);
  const h = Math.floor(total / 60);
  const m = total % 60;
  return `${h}h ${m.toString().padStart(2, "0")}m`;
}
